import { ModuleWithProviders, NgModule } from '@angular/core';
import { FormlyBootstrapModule } from '@ngx-formly/bootstrap';
import { ConfigOption, FORMLY_CONFIG } from '@ngx-formly/core';
import { TranslateService } from '@ngx-translate/core';
import { PasswordToggleFormlyExtension } from './extension/password-toggle.formly-extension';
import { PrependAppendFormlyExtension } from './extension/prepend-append.formly-extension';
import { TranslateFormlyExtension } from './extension/translate.formly-extension';
import { ButtonFieldComponent } from './field/button-field/button-field.component';
import { CardContentFieldComponent } from './field/card-content-field/card-content-field.component';
import { CheckboxFieldComponent } from './field/checkbox-field/checkbox-field.component';
import { DatepickerFieldComponent } from './field/datepicker-field/datepicker-field.component';
import { DonutChartFieldComponent } from './field/donut-chart-field/donut-chart-field.component';
import { DurationCardIndicatorFieldComponent } from './field/duration-card-indicator-field/duration-card-indicator-field.component';
import { InformationCardRowFieldComponent } from './field/information-card-row-field/information-card-row-field.component';
import { LabelFieldComponent } from './field/label-field/label-field.component';
import { MultiSelectFieldComponent } from './field/multi-select-field/multi-select-field.component';
import { NumberInputFieldComponent } from './field/number-input-field/number-input-field.component';
import { RegulatoryDisclaimerFieldComponent } from './field/regulatory-disclaimer-field/regulatory-disclaimer-field.component';
import { SectionLabelFieldComponent } from './field/section-label-field/section-label-field.component';
import { SelectButtonFieldComponent } from './field/select-button-field/select-button-field.component';
import { SelectFieldComponent } from './field/select-field/select-field.component';
import { ShortLabelFieldComponent } from './field/short-label-field/short-label-field.component';
import { SliderInputFieldComponent } from './field/slider-input-field/slider-input-field.component';
import { SmallLabelFieldComponent } from './field/small-label-field/small-label-field.component';
import { TextAreaFieldComponent } from './field/text-area-field/text-area-field.component';
import { TextInputFieldComponent } from './field/text-input-field/text-input-field.component';
import { VideoFieldComponent } from './field/video-field/video-field.component';
import { phoneFormlyValidator } from './validator/phone.formly-validator';
import { CardFormlyWrapperComponent } from './wrapper/card-formly-wrapper';
import { FormGroupFormlyWrapperComponent } from './wrapper/form-group-formly-wrapper';
import { RowFormlyWrapperComponent } from './wrapper/row-formly-wrapper';
import { TabsWrapperComponent } from './wrapper/tabs-formly-wrapper';

// for v7, leave just formlyConfigFactory and change the name of this file to `formly.config.ts`
export function formlyConfigFactory(translate: TranslateService): ConfigOption {
  return {
    types: [
      {
        name: 'select-button',
        component: SelectButtonFieldComponent,
      },
      {
        name: 'label',
        component: LabelFieldComponent,
      },
      {
        name: 'short-label',
        component: ShortLabelFieldComponent,
      },
      {
        name: 'small-label',
        component: SmallLabelFieldComponent,
      },
      {
        name: 'section-label',
        component: SectionLabelFieldComponent,
      },
      {
        name: 'regulatory-disclaimer',
        component: RegulatoryDisclaimerFieldComponent,
      },
      {
        name: 'slider',
        component: SliderInputFieldComponent,
      },
      {
        name: 'select',
        component: SelectFieldComponent,
      },
      {
        name: 'multi-select',
        component: MultiSelectFieldComponent,
      },
      {
        name: 'checkbox',
        component: CheckboxFieldComponent,
      },
      {
        name: 'textarea',
        component: TextAreaFieldComponent,
      },
      {
        name: 'text-input',
        component: TextInputFieldComponent,
      },
      {
        name: 'number-input',
        component: NumberInputFieldComponent,
      },
      {
        name: 'datepicker',
        component: DatepickerFieldComponent,
      },
      {
        name: 'donut-chart',
        component: DonutChartFieldComponent,
      },
      {
        name: 'information-card-row',
        component: InformationCardRowFieldComponent,
      },
      {
        name: 'duration-card-indicator',
        component: DurationCardIndicatorFieldComponent,
      },
      {
        name: 'card-content',
        component: CardContentFieldComponent,
      },
      {
        name: 'button',
        component: ButtonFieldComponent,
      },
      {
        name: 'video',
        component: VideoFieldComponent,
      },
    ],
    extensions: [
      {
        name: 'password-toggle',
        extension: new PasswordToggleFormlyExtension(),
      },
      {
        name: 'prepend-append',
        extension: new PrependAppendFormlyExtension(),
      },
      {
        name: 'translate',
        extension: new TranslateFormlyExtension(),
      },
    ],
    wrappers: [
      { name: 'form-group', component: FormGroupFormlyWrapperComponent },
      {
        name: 'tabs',
        component: TabsWrapperComponent,
      },
      {
        name: 'card',
        component: CardFormlyWrapperComponent,
      },
      {
        name: 'row',
        component: RowFormlyWrapperComponent,
      },
    ],
    validators: [
      {
        name: 'phone',
        validation: phoneFormlyValidator,
      },
    ],
    validationMessages: [
      {
        name: 'required',
        message: () => translate.stream('VALIDATION.REQUIRED'),
      },
      {
        name: 'pattern',
        message: () => translate.stream('VALIDATION.PATTERN'),
      },
      {
        name: 'min',
        message: (_err, field) =>
          translate.stream('VALIDATION.MIN', { number: field.props?.min }),
      },
      {
        name: 'max',
        message: (_err, field) =>
          translate.stream('VALIDATION.MAX', { number: field.props?.max }),
      },
      {
        name: 'minLength',
        message: (_err, field) =>
          translate.stream('VALIDATION.MIN_LENGTH', {
            number: field.props?.minLength,
          }),
      },
      {
        name: 'maxLength',
        message: (_err, field) =>
          translate.stream('VALIDATION.MAX_LENGTH', {
            number: field.props?.maxLength,
          }),
      },
    ],
    extras: {
      immutable: false,
    },
  };
}

@NgModule({
  imports: [LabelFieldComponent, FormlyBootstrapModule],
  providers: [
    {
      provide: FORMLY_CONFIG,
      useFactory: formlyConfigFactory,
      deps: [TranslateService],
      multi: true,
    },
  ],
})
export class FormlyConfigModule {
  static forRoot(): ModuleWithProviders<FormlyConfigModule> {
    return {
      ngModule: FormlyConfigModule,
      providers: [],
    };
  }
}
